import { UseQueryOptions } from '@tanstack/react-query';
import { isBoolean, isFunction, isNil, omit } from 'lodash-es';
import { OneServiceOptions, OneServiceOptionsCore, OneServiceOptionsNoParams } from 'types/services/services';

/**
 * Check if the service is enabled
 * @param options service options
 * @param params params of the service
 * @returns true if the service is enabled
 */
const isOptionEnabled = <Data, Params extends object>(
  options: Params extends object ? OneServiceOptions<Data, Params> : OneServiceOptionsNoParams<Data>,
  params?: Params,
): boolean => {
  if (!isNil(params)) {
    const { enabled } = options as OneServiceOptions<Data, Params>;

    if (isFunction(enabled)) {
      return enabled(params);
    }
  }

  if (isNil(params)) {
    const { enabled } = options as OneServiceOptionsNoParams<Data>;

    if (isFunction(enabled)) {
      return enabled();
    }
  }

  const { enabled } = options;

  if (isBoolean(enabled)) {
    return enabled;
  }

  return true;
};

type OptionResult<Data> = Pick<UseQueryOptions<Data>, 'enabled'> & OneServiceOptionsCore<Data>;

/**
 * Create Tanstack query options from options of a service without params.
 * @param options options of a service without params
 * @returns Tanstack query options
 */
function serviceOptionsToTQOptions<Data>(options: OneServiceOptionsNoParams<Data> | undefined): OptionResult<Data> | undefined;
/**
 * Create Tanstack query options from options of a service with params.
 * @param options options of a service with params
 * @param params params of the service
 * @returns Tanstack query options
 */
function serviceOptionsToTQOptions<Data, Params extends object>(
  options: OneServiceOptions<Data, Params> | undefined,
  params: Params,
): OptionResult<Data> | undefined;
/**
 * Create Tanstack query options from options of a service.
 * @param options options of a service
 * @param params params of the service
 * @returns Tanstack query options
 */
function serviceOptionsToTQOptions<Data, Params extends object>(
  options: (Params extends object ? OneServiceOptions<Data, Params> : OneServiceOptionsNoParams<Data>) | undefined,
  params?: Params,
): OptionResult<Data> | undefined {
  if (isNil(options)) {
    return undefined;
  }

  const isEnabled = isOptionEnabled(options, params);

  return { ...omit(options, ['enabled']), enabled: isEnabled };
}

export default serviceOptionsToTQOptions;
